import { createContext, useContext, useCallback, useEffect, useRef, useState } from 'react';
import { masterclassApi, errorMessage } from '../masterclasses/api';

export const PILOT_API = '/admin/interactive-lessons/erc-piloto';
export const FusedApiContext = createContext(PILOT_API);

export const useFusedLesson = (apiBase = PILOT_API) => {
  const [lesson, setLesson] = useState(null);
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const lock = useRef(false);
  const alive = useRef(true);
  const initialize = useCallback(async () => {
    if (lock.current) return;
    lock.current = true; setBusy(true); setError('');
    try {
      const [info, session] = await Promise.all([masterclassApi.get(apiBase), masterclassApi.post(`${apiBase}/sessions`)]);
      if (alive.current) { setLesson(info.data); setState(session.data); }
    } catch (err) { if (alive.current) setError(errorMessage(err)); }
    finally { lock.current = false; if (alive.current) setBusy(false); }
  }, [apiBase]);
  useEffect(() => { alive.current = true; initialize(); return () => { alive.current = false; }; }, [initialize]);
  const run = async (operation) => {
    if (lock.current) return;
    lock.current = true; setBusy(true); setError('');
    try { const { data } = await operation(); if (alive.current) setState(data); }
    catch (err) {
      if (alive.current) setError(errorMessage(err));
      if (err.response?.status === 409) {
        try { const [info, session] = await Promise.all([masterclassApi.get(apiBase), masterclassApi.post(`${apiBase}/sessions`)]); if (alive.current) { setLesson(info.data); setState(session.data); } }
        catch (_) { /* Preserve the original visible error until an explicit retry. */ }
      }
    } finally { lock.current = false; if (alive.current) setBusy(false); }
  };
  const view = (index) => run(() => masterclassApi.get(`${apiBase}/sessions/${state.session_id}`, { params: { step: index } }));
  const answer = (option) => run(() => masterclassApi.post(`${apiBase}/sessions/${state.session_id}/answer`, {
    step_id: state.step.id, version: state.version, option,
  }));
  const next = () => state.view_index < state.current_index ? view(state.view_index + 1) : run(() => masterclassApi.post(`${apiBase}/sessions/${state.session_id}/next`, {
    step_id: state.step.id, version: state.version,
  }));
  const restart = () => run(() => masterclassApi.post(`${apiBase}/restart`, { session_id: state.session_id, confirmed: true }));
  return { lesson, state, busy, error, initialize, answer, next, view, restart };
};

export const useSlideImage = (page) => {
  const apiBase = useContext(FusedApiContext);
  const [url, setUrl] = useState(null);
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    if (!page) return undefined;
    const controller = new AbortController();
    let blobUrl;
    setUrl(null); setError('');
    masterclassApi.get(`${apiBase}/assets/slide-${page}`, { responseType: 'blob', signal: controller.signal })
      .then(({ data }) => { if (!controller.signal.aborted) { blobUrl = URL.createObjectURL(data); setUrl(blobUrl); } })
      .catch(() => { if (!controller.signal.aborted) setError('No se pudo cargar la diapositiva.'); });
    return () => { controller.abort(); if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [page, attempt, apiBase]);
  return { url, error, retry: () => setAttempt(value => value + 1) };
};